import { DataRow } from './DataRow';

import { usePolicyInsightContext } from './PolicyInsight';

import { formatCurrencyValue } from './policyInsightUtils';

export function TotalInvestedIntoPolicy() {
  const { insightData } = usePolicyInsightContext();

  const monthlyPremium = insightData?.monthlyPremium;
  const policyDuration = insightData?.policyDuration;
  const monthlyPremiumTotalCost = insightData?.monthlyPremiumTotalCost;
  const lumpSumPayment = insightData?.lumpSumPayment;
  const predictedTotalPremiumsPaid = insightData?.predictedTotalPremiumsPaid;
  const isLumpSumOnly = insightData?.isLumpSumOnly;

  const hasMonthlyPremium = monthlyPremium && monthlyPremium > 0;
  const hasLumpSum = lumpSumPayment && lumpSumPayment > 0;
  const hasBothPremiumAndLumpSum = hasMonthlyPremium && hasLumpSum;

  const getHelperText = () => {
    if (isLumpSumOnly) {
      return undefined;
    }
    if (hasBothPremiumAndLumpSum && monthlyPremiumTotalCost) {
      return `(${formatCurrencyValue(monthlyPremiumTotalCost)} + ${formatCurrencyValue(lumpSumPayment)})`;
    }
    if (hasMonthlyPremium && predictedTotalPremiumsPaid) {
      return `(${policyDuration} x ${formatCurrencyValue(monthlyPremium)})`;
    }
    return undefined;
  };

  if (isLumpSumOnly) {
    return (
      <section>
        <h2 className="mb-4 text-lg font-semibold text-gray-950">Total Invested Into Policy</h2>
        <div className="space-y-4 rounded-xl border border-gray-200 p-4">
          <DataRow
            highlight="red"
            label="Lump Sum Payment"
          >
            {formatCurrencyValue(lumpSumPayment)}
          </DataRow>
          <hr className="border-gray-200" />
          <p className="text-sm italic text-gray-700">
            This is a single lump sum payment into the policy.
          </p>
        </div>
      </section>
    );
  }

  const getMonthlyPremiumHelperText = () => {
    if (hasBothPremiumAndLumpSum) {
      return `(${policyDuration} x ${formatCurrencyValue(monthlyPremium)})`;
    }
    return undefined;
  };

  return (
    <section>
      <h2 className="mb-4 text-lg font-semibold text-gray-950">Total Invested Into Policy</h2>
      <div className="space-y-4 rounded-xl border border-gray-200 p-4">
        <DataRow label="Monthly Premium">{formatCurrencyValue(monthlyPremium)}</DataRow>
        <hr className="border-gray-200" />
        <DataRow label="Policy Duration">{policyDuration}</DataRow>
        {hasMonthlyPremium && (
          <>
            <hr className="border-gray-200" />
            <DataRow
              label="Predicted Total Monthly Premiums"
              helperText={getMonthlyPremiumHelperText()}
            >
              {formatCurrencyValue(monthlyPremiumTotalCost ?? predictedTotalPremiumsPaid)}
            </DataRow>
          </>
        )}
        {hasLumpSum && (
          <>
            <hr className="border-gray-200" />
            <DataRow label="Lump Sum Payment">{formatCurrencyValue(lumpSumPayment)}</DataRow>
          </>
        )}
        <hr className="border-gray-200" />
        <DataRow
          highlight="red"
          label="Predicted Total Premiums Paid"
          helperText={getHelperText()}
        >
          {formatCurrencyValue(predictedTotalPremiumsPaid)}
        </DataRow>
        <hr className="border-gray-200" />
        <p className="text-sm italic text-gray-700">
          This is the predicted total amount you'll pay in premiums before your policy activates.
        </p>
      </div>
    </section>
  );
}
